// Piezas compartidas por las páginas de la sección Asesoras del admin
// ("Pagos asesoras" y "Prospectos").
import { useEffect, useState } from 'react';
import { Box, Button, Flex, Select, Text } from '@chakra-ui/react';
import dayjs from 'dayjs';
import xhr from 'xhr';

export const BRAND = '#574feb';
export const PAGE_SIZE = 20;
const FALLBACK_USD_TO_COP = 4186.71; // si falla la API de la tasa

const getUsdToCopRate = async () => {
  try {
    const resp = await fetch('https://economia.awesomeapi.com.br/json/last/USD-COP');
    if (!resp.ok) throw new Error('Error al obtener la tasa');
    const rate = parseFloat((await resp.json())?.USDCOP?.bid);
    if (!isNaN(rate)) return rate;
  } catch (e) {
    console.warn('Falló la tasa dinámica, se usa la de respaldo:', e);
  }
  return FALLBACK_USD_TO_COP;
};

export const useUsdToCopRate = () => {
  const [rate, setRate] = useState(FALLBACK_USD_TO_COP);
  useEffect(() => {
    getUsdToCopRate().then(setRate);
  }, []);
  return rate;
};

// Asesoras activas, ordenadas por nombre.
export const useActiveAdvisers = () => {
  const [advisers, setAdvisers] = useState([]);
  useEffect(() => {
    xhr('/advisers?filters[active][$eq]=true&pagination[pageSize]=100&populate=user')
      .then((res) =>
        setAdvisers(
          (res?.data?.data || [])
            .filter((el) => el.user)
            .map((el) => ({ id: el.id, name: `${el.user.first_name || ''} ${el.user.last_name || ''}`.trim() }))
            .sort((a, b) => a.name.localeCompare(b.name, 'es'))
        )
      )
      .catch((err) => console.error('Error cargando asesoras:', err));
  }, []);
  return advisers;
};

export const cop = (value) =>
  Number(value || 0).toLocaleString('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0, maximumFractionDigits: 0 });

export const RANGES = [
  { key: 'mes', label: 'Este mes', range: () => [dayjs().startOf('month'), dayjs()] },
  {
    key: 'anterior',
    label: 'Mes anterior',
    range: () => [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')],
  },
  { key: '3meses', label: 'Últimos 3 meses', range: () => [dayjs().subtract(3, 'month').startOf('day'), dayjs()] },
];
export const toInput = (d) => d.format('YYYY-MM-DD');

export const MATCH_LABEL = { correo: 'por correo', celular: 'por celular' };

// Fuente de la campaña (UTM). Si la registró la asesora, lo indica.
export const sourceLabel = (origin) => {
  if (!origin) return '';
  const utm = [origin.utm_source, origin.utm_medium].filter(Boolean).join(' / ');
  return utm && origin.utm_manual ? `${utm} (asesora)` : utm;
};

// Línea secundaria bajo la fuente: la campaña (el anuncio va en su propia columna).
export const sourceDetail = (origin) => (origin ? origin.utm_campaign || '' : '');

// Celda «ID anuncio»: el anuncio de la campaña (utm_content), automático o registrado por la asesora.
export function AdIdCell({ origin }) {
  if (!origin?.utm_content) return <Dash />;
  return (
    <>
      <Text fontSize="sm" sx={{ fontVariantNumeric: 'tabular-nums' }} wordBreak="break-all">
        {origin.utm_content}
      </Text>
      {origin.utm_manual && (
        <Text fontSize="xs" color="gray.500">
          Registrado por asesora
        </Text>
      )}
    </>
  );
}

export const downloadCsv = (header, lines, filename) => {
  const escape = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
  const csv = [header, ...lines].map((line) => line.map(escape).join(';')).join('\r\n');
  const blob = new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

export function KpiTile({ label, value, hint }) {
  return (
    <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="lg" px={5} py={4}>
      <Text fontSize="xs" fontWeight="600" color="gray.500" textTransform="uppercase" letterSpacing="0.06em">
        {label}
      </Text>
      <Text fontSize="2xl" fontWeight="700" color="gray.800" mt={1} sx={{ fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </Text>
      {hint && (
        <Text fontSize="xs" color="gray.500" mt={0.5}>
          {hint}
        </Text>
      )}
    </Box>
  );
}

export const Dash = () => (
  <Text as="span" color="gray.300">
    —
  </Text>
);

export function AdviserSelect({ advisers, value, onChange }) {
  return (
    <Select value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">Todas las asesoras</option>
      <option value="none">Sin asesora asignada</option>
      {advisers.map((el) => (
        <option key={el.id} value={el.id}>
          {el.name}
        </option>
      ))}
    </Select>
  );
}

export function QuickRanges({ active, onSelect }) {
  return (
    <Flex mt={3} gap={2} wrap="wrap" align="center">
      <Text fontSize="xs" color="gray.500" mr={1}>
        Rango rápido:
      </Text>
      {RANGES.map((range) => (
        <Button
          key={range.key}
          size="xs"
          borderRadius="full"
          variant={active === range.key ? 'solid' : 'outline'}
          colorScheme="purple"
          onClick={() => onSelect(range)}
        >
          {range.label}
        </Button>
      ))}
    </Flex>
  );
}
